import { IconFont, pointer, Tooltip } from '@byecode/ui'
import type { FieldInputADTValue, TextValue } from '@lighthouse/core'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { FieldIconTypeMap } from '../../../constants'
import { getJumpUrl } from '../../../utils'
import { useAppThemeContext } from '../../ApplicationContainer'
import { applyFillPickerValue4CssColorValue } from '../../FillPicker'
import BaseInput from '../BaseInput'
import { fieldLabelMap } from '../constant'
import { useFieldInputFont } from '../hooks/useFieldInputFont'
import type { FieldBaseProps } from '../types'

interface SimpleTextFieldProps extends FieldBaseProps {
    value: TextValue
}

const SCxIconWrapper = styled.div`
    display: flex;
    align-items: center;
    ${pointer}
`

type SimpleTextFieldType = 'phoneNumber' | 'url' | 'email'

const SimpleTextField: React.FunctionComponent<SimpleTextFieldProps> = props => {
    const {
        onBlur,
        onChange,
        value,
        config: { size, placeholder, inputType, breakPoint, autofocus },
        readOnly,
        onKeyDown,
        onFocus
    } = props

    const { theme } = useAppThemeContext()
    const inputRef = useRef<HTMLTextAreaElement>(null)
    const [isHover, setIsHover] = useState(false)
    const [isFocus, setIsFocus] = useState(false)
    const placeholderColor = applyFillPickerValue4CssColorValue(breakPoint?.design?.placeholderColor?.color, theme.palettes)
    const { customFontSize, customFontFamily, customFontWeight } = useFieldInputFont(breakPoint)

    const handleChange = useCallback(
        (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
            const { value } = ev.target
            onChange?.({ type: inputType, value } as FieldInputADTValue)
        },
        [inputType, onChange]
    )

    const handleBlur = useCallback(() => {
        setIsFocus(false)
        onBlur?.({ type: inputType, value } as FieldInputADTValue)
    }, [inputType, onBlur, value])

    const handleFocus = useCallback(() => {
        setIsFocus(true)
        onFocus?.()
    }, [onFocus])

    const href = useMemo(() => {
        if (!value) {
            return ''
        }
        switch (inputType) {
            case 'phoneNumber': {
                return `tel:${value}`
            }
            case 'email': {
                return `mailto:${value}`
            }
            case 'url': {
                return getJumpUrl(value)
            }
            default: {
                return ''
            }
        }
    }, [inputType, value])

    const isShowRightSection = useMemo(() => (isHover && !readOnly) || isFocus, [isFocus, isHover, readOnly])

    const handleNavigate = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            e.stopPropagation()
            if (!href) {
                return
            }
            if (inputType === 'url') {
                window.open(href)
                return
            }
            window.location.href = href
        },
        [inputType, href]
    )

    return (
        <BaseInput
            value={value}
            size={size}
            isWrap={false}
            readOnly={readOnly}
            data-field-input
            data-field-active={isFocus}
            data-field-disabled={!!readOnly}
            placeholderColor={placeholderColor}
            fontSize={customFontSize}
            fontFamily={customFontFamily}
            fontWeight={customFontWeight}
            inputProps={{
                autoFocus: autofocus,
                placeholder,
                onChange: handleChange,
                onMouseEnter: () => {
                    !readOnly && setIsHover(true)
                },
                onMouseLeave: () => {
                    setIsHover(false)
                },
                onFocus: handleFocus,
                onBlur: handleBlur,
                onKeyDown
            }}
            ref={inputRef}
            rightSection={
                isShowRightSection && (
                    <div
                        style={{ display: 'flex', alignItems: 'center' }}
                        onMouseEnter={() => {
                            !readOnly && setIsHover(true)
                        }}
                        onMouseLeave={() => {
                            setIsHover(false)
                        }}
                        data-field-icon
                    >
                        <Tooltip offset={16} title={fieldLabelMap.get(inputType) ?? ''}>
                            <SCxIconWrapper onClick={handleNavigate}>
                                <IconFont type={FieldIconTypeMap[inputType]} />
                            </SCxIconWrapper>
                        </Tooltip>
                    </div>
                )
            }
        />
    )
}

export default SimpleTextField
